import React, { useEffect, useMemo } from 'react';
import { formatCurrency, safelyParseFloat } from '@/utils/constants';
import { Grid } from '@mui/material';
import PolicyTotalHeader from '../InsightPolicyTable/TotalCard';
import { useSearchInsightPolicyTransactionsInfiniteQuery } from '@/features/policies/policiesApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { FILTER_TYPES } from '@/features/policies/data';
import { safelyParseDate } from '@/utils/date';

const InvoiceTotalsHeader = ({ displayOnlyPosted = false }) => {
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const filters = useSelector((state) => state.debtorsFilterPrefs);

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const {
		data,
		fetchNextPage,
		isLoading,
		isFetching,
		isFetchingNextPage,
		isUninitialized,
		hasNextPage,
		isError,
	} = useSearchInsightPolicyTransactionsInfiniteQuery(
		{
			...(!userDetails?.canViewGroup && { authRepId: userDetails?.authorisedRepId }),
			ownerDocIds: filters?.ownerIds,
			fundingStatus: filters.fundingStatus,
			filterBy: FILTER_TYPES.ShowOutstanding,
			displayOnlyPosted: displayOnlyPosted,
		},
		{ skip: !userDetails || userQuery.isFetching },
	);

	useEffect(() => {
		if (isUninitialized || !data?.pages?.length || !hasNextPage || isFetching) {
			return;
		}

		fetchNextPage();
	}, [data?.pages?.length, hasNextPage, isFetching, isUninitialized, fetchNextPage]);

	const isTotalsLoading =
		!isError &&
		(isUninitialized || isLoading || isFetching || isFetchingNextPage || Boolean(hasNextPage));

	const allPageData = [...(data?.pages.flatMap((r) => r.Results ?? []) ?? [])].map((r) => ({
		...r,
		...(r.ClientDocId && { clientDocId: r.ClientDocId }),
		...(r.OwnerDocId && { ownerDocId: r.OwnerDocId }),
		RenewalDate: safelyParseDate(r?.PolicyToDate ?? r?.ToDate)?.getTime?.(),
	}));

	const options = [
		{ label: 'due 45+ days', days: 45 },
		{ label: 'due 60+ days', days: 60 },
		{ label: 'due 90+ days', days: 90 },
	];

	const { totalBalance, rangeTotals } = useMemo(() => {
		const today = new Date();
		const todayTimestamp = new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate(),
		).getTime();
		const MS_PER_DAY = 24 * 60 * 60 * 1000;

		const totals = options.reduce((acc, option) => {
			acc[option.days] = 0;
			return acc;
		}, {});

		let total = 0;

		const calculateDaysOutstanding = (policyEffectiveDate) => {
			if (!policyEffectiveDate) return 0;
			const policyDate = safelyParseDate(policyEffectiveDate);
			if (!policyDate) return 0;
			const policyTimestamp = new Date(
				policyDate.getFullYear(),
				policyDate.getMonth(),
				policyDate.getDate(),
			).getTime();
			return (todayTimestamp - policyTimestamp) / MS_PER_DAY;
		};

		allPageData.forEach((item) => {
			const balance = safelyParseFloat(item.Balance ?? 0);
			//Don't include negative values
			if (balance <= 0) return;

			if (!item.PolicyEffectiveDate) {
				total += balance;
				return;
			}

			const daysOutstanding = calculateDaysOutstanding(item.PolicyEffectiveDate);
			if (daysOutstanding < 0) return;

			total += balance;
			options.forEach(({ days }) => {
				if (daysOutstanding >= days) totals[days] += balance;
			});
		});

		return { totalBalance: total, rangeTotals: totals };
	}, [allPageData]);

	return (
		<Grid size={{ xs: 12 }} container rowSpacing={1} columnSpacing={2} width='100%'>
			<Grid size={{ sm: 12, md: 3 }}>
				<PolicyTotalHeader
					title='Total balance due'
					total={formatCurrency({ num: totalBalance })}
					isLoading={isTotalsLoading}
					isError={isError}
				/>
			</Grid>
			{options.map((option) => (
				<Grid size={{ sm: 12, md: 3 }} key={option.days}>
					<PolicyTotalHeader
						title={option.label}
						tooltip={`${option.days}+ days: The total outstanding balance of invoices with an effective date at least ${option.days} days before today's date.`}
						total={formatCurrency({ num: rangeTotals[option.days] })}
						isLoading={isTotalsLoading}
						isError={isError}
					/>
				</Grid>
			))}
		</Grid>
	);
};

export default InvoiceTotalsHeader;
